import { Fragment, useRef, useState, type DragEvent } from "react";
import { useDrop } from "@norkron/web-dnd";
import type { CommentStatus, Ticket, TicketStatus } from "../../api/types";
import { isDroppable, isWritableStatus } from "../../lib/kanban";
import { statusLabel } from "../../lib/tickets";
import { KanbanCard } from "./KanbanCard";

/**
 * A kanban column = one status (design tickets-kanban §4.1). The 5 human-settable
 * statuses are DROP TARGETS (`useDrop` from `@norkron/web-dnd`, accept `ticket`);
 * only the system-derived `orphaned` status is drag-OUT-only — its `useDrop` is
 * disabled and the drop `bind` is NOT spread, so it carries no `data-dnd-target`
 * and rejects drops entirely (respecting the `validStatus` server contract).
 * Header = status dot + label + live count; body = the cards, or a
 * present-but-empty affordance.
 *
 * DROP PLACEHOLDER (design §4.4): while a card is dragged over a droppable column
 * we render a ghost slot at the exact insertion index — computed from the pointer
 * Y vs. each card's vertical midpoint — and the cards below reflow to open the
 * gap (the placeholder animates its height open, so the neighbours ride down
 * smoothly; `prefers-reduced-motion` snaps instead). We compose the lib's
 * drag/drop event wiring — no hand-rolled DnD engine.
 */

/** Insertion index for the pointer at `clientY` among the real cards (elements
 *  carrying `data-dnd-id`) inside `container`: the index of the first card whose
 *  vertical midpoint sits below the pointer, else the end. The ghost placeholder
 *  is excluded (it has no `data-dnd-id`), so the measurement never feeds back on
 *  itself. Exported for unit testing without a DOM harness's DnD layer. */
export function insertionIndexAt(
  container: HTMLElement,
  clientY: number,
): number {
  const cards = Array.from(
    container.querySelectorAll<HTMLElement>("[data-dnd-id]"),
  );
  for (let i = 0; i < cards.length; i++) {
    const rect = cards[i].getBoundingClientRect();
    if (clientY < rect.top + rect.height / 2) return i;
  }
  return cards.length;
}

export function KanbanColumn({
  status,
  tickets,
  slug,
  onMove,
}: {
  status: TicketStatus;
  tickets: Ticket[];
  slug: string;
  onMove: (ticketId: number, status: CommentStatus) => void;
}) {
  const droppable = isDroppable(status);
  // The live insertion slot for the ghost placeholder; null when nothing is
  // hovering this column.
  const [placeholderIndex, setPlaceholderIndex] = useState<number | null>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  const drop = useDrop({
    accept: ["ticket"],
    disabled: !droppable,
    onDrop: (payload) => {
      // Close the gap, then land the card in this column (self-drop guarded in
      // moveTicket).
      setPlaceholderIndex(null);
      if (isWritableStatus(status)) onMove(Number(payload.id), status);
    },
    onDragLeave: () => setPlaceholderIndex(null),
  });
  // Only writable columns are drop targets — never spread the bind on a system
  // column, so `data-dnd-target` is absent there regardless of the hook's guard.
  const dropBind = droppable ? drop.bind : {};

  // Track the insertion slot live as the pointer moves over the cards area.
  const onCardsDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!droppable) return;
    e.preventDefault();
    setPlaceholderIndex(insertionIndexAt(e.currentTarget, e.clientY));
  };
  // Close the gap only when the pointer truly leaves the column (not when it
  // crosses between child cards — that fires dragleave with a descendant
  // relatedTarget, which we ignore).
  const onCardsDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setPlaceholderIndex(null);
    }
  };

  const showPlaceholder = droppable && placeholderIndex !== null;
  const placeholderEl = (
    <div
      className="kanban-col__placeholder"
      data-testid={`kanban-placeholder-${status}`}
      aria-hidden
    />
  );

  return (
    <section
      {...dropBind}
      className={`kanban-col kanban-col--${status}${
        droppable ? "" : " kanban-col--system"
      }${droppable && drop.isOver ? " kanban-col--over" : ""}`}
      data-testid={`kanban-column-${status}`}
      aria-label={`${statusLabel(status)} — ${tickets.length} ticket${
        tickets.length === 1 ? "" : "s"
      }`}
    >
      <header className="kanban-col__head">
        <span className={`badge badge--status-${status} kanban-col__label`}>
          <span className="badge__dot" aria-hidden />
          {statusLabel(status)}
        </span>
        <span
          className="kanban-col__count"
          data-testid={`kanban-column-count-${status}`}
        >
          {tickets.length}
        </span>
      </header>

      {droppable ? null : (
        <p className="kanban-col__system">System-managed — drag out only</p>
      )}

      <div
        className="kanban-col__cards"
        ref={cardsRef}
        onDragOver={onCardsDragOver}
        onDragLeave={onCardsDragLeave}
      >
        {tickets.length > 0 ? (
          tickets.map((ticket, i) => (
            <Fragment key={ticket.id}>
              {showPlaceholder && placeholderIndex === i ? placeholderEl : null}
              <KanbanCard ticket={ticket} slug={slug} onMove={onMove} />
            </Fragment>
          ))
        ) : showPlaceholder ? null : (
          <p
            className="kanban-col__empty"
            data-testid={`kanban-column-empty-${status}`}
          >
            {droppable ? "Drop tickets here" : "None"}
          </p>
        )}
        {/* trailing slot (drop at/after the last card, incl. the empty column) */}
        {showPlaceholder && placeholderIndex >= tickets.length
          ? placeholderEl
          : null}
      </div>
    </section>
  );
}
